import { useQuery } from "@tanstack/react-query";
import type { StoreListResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

// The store multi-select loads options client-side and MultiSelect filters
// them in-popover. The admin stores endpoint caps `limit` at 100 and has no
// free-text search, so v1 loads the first 100 stores. `total` lets the caller
// warn when there are more stores than were loaded (extend with a server-side
// searchable Combobox when a deployment outgrows this).
const STORE_OPTIONS_LIMIT = 100;

export type StoreOption = {
  value: string;
  label: string;
  storeSlug: string;
};

export function useStoreOptionsQuery(enabled: boolean) {
  const query = useQuery({
    queryKey: queryKeys.admin.stores({ limit: STORE_OPTIONS_LIMIT }),
    queryFn: () =>
      apiJsonClient<StoreListResponse>(
        `${endpoints.admin.stores}?limit=${STORE_OPTIONS_LIMIT}`,
      ),
    enabled,
  });

  const options: StoreOption[] = (query.data?.items ?? []).map((store) => ({
    value: store.id,
    label: store.storeName,
    storeSlug: store.storeSlug,
  }));

  return {
    options,
    total: query.data?.total ?? 0,
    loadedCount: options.length,
    isLoading: query.isLoading,
    error: query.error,
  };
}
